import type { Presentation, Slide } from '@features/powerpoint';
import { titleSlideV2 } from '../../../../slides/title-slide-v2';
import {
  createSharedPresentationConfigV2,
  MASTER_NAMES,
} from '../../../../utils/shared-presentation-config-v2';
import { overviewSlideFromPropsV2 } from './build-overview-slide';
import { executiveSummarySlideFromPropsV2 } from './build-executive-summary-slide';
import { salesAttributionSlidesV2 } from '../../slides/v2/sales-attribution-slides';
import { campaignAnalyticsSlidesV2 } from '@features/lci-report/subdomains/visitation/slides/v2/campaign-analytics/build-campaign-analytics-slides-v2';
import { appendixSlidesFromPropsV2 } from '../../slides/v2/appendix/config';
import { visitLiftTitleSlideV2 } from '@features/lci-report/subdomains/visitation/slides/v2/visit-attribution/visit-lift-title-slide';
import { overallCampaignResultsSlideV2 } from '@features/lci-report/subdomains/visitation/slides/v2/visit-attribution/overall-campaign-results';
import type { BuildPresentationProps } from '../build-presentation';
import { applyYoutubeTransformations } from '../build-presentation';

const visitLiftTitleSlideFromProps = (props: BuildPresentationProps): Slide[] =>
  props.report.hasVisitMetrics &&
  props.customFields.includedSlides.footTraffic.titleSlide
    ? [visitLiftTitleSlideV2()]
    : [];

const overallCampaignResultsFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] =>
  props.report.hasVisitMetrics &&
  props.customFields.includedSlides.footTraffic.overallResults
    ? [
        overallCampaignResultsSlideV2({
          slideMaster,
          totalVisits: props.visitMetrics.projectedVisits ?? 0,
          incrementalVisits: props.visitMetrics.incrementalVisits ?? 0,
          incrementalLift: props.visitMetrics.incrementalLift ?? 0,
          costPerVisit: props.calculatedFields.costPerVisit ?? 0,
          costPerIncrementalVisit:
            props.calculatedFields.costPerIncrementalVisit ?? 0,
          confidenceLevel: props.visitMetrics.confidenceLevel ?? 0,
          ftaOverallResults: props.customFields.ftaOverallResults,
        }),
      ]
    : [];

export const buildPresentationV2 = (
  props: BuildPresentationProps
): Presentation => {
  const transformedProps = applyYoutubeTransformations(props);

  const sharedConfig = createSharedPresentationConfigV2({
    campaign: transformedProps.campaign,
    brandLogo: transformedProps.brandLogo,
  });

  return {
    ...sharedConfig,
    filename: `${transformedProps.campaign.name}_lci_report_${transformedProps.selectedReportDate}.pptx`,
    slides: [
      titleSlideV2({
        campaign: {
          id: transformedProps.campaign.id,
          name: transformedProps.campaign.name,
        },
        brandLogo: transformedProps.brandLogo,
      }),
      ...overviewSlideFromPropsV2(
        transformedProps,
        MASTER_NAMES.titleAndContent
      ),
      ...executiveSummarySlideFromPropsV2(
        transformedProps,
        MASTER_NAMES.titleAndContent
      ),
      ...visitLiftTitleSlideFromProps(transformedProps),
      ...overallCampaignResultsFromProps(
        transformedProps,
        MASTER_NAMES.titleAndContent
      ),
      ...salesAttributionSlidesV2(
        transformedProps,
        MASTER_NAMES.titleAndContent
      ),
      ...campaignAnalyticsSlidesV2(
        transformedProps,
        MASTER_NAMES.titleAndContent
      ),
      ...appendixSlidesFromPropsV2(transformedProps),
    ],
  };
};
